import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type React from "react";

import type { NotificationItem, NotificationsResponse } from "../types";
import { api } from "../utils/api";

const POLL_INTERVAL_MS = 60_000;

function formatRelative(iso: string | null) {
  if (!iso) return "Unknown time";
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return "Unknown time";
  const diffMs = Date.now() - value.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin <= 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.round(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return value.toLocaleDateString();
}

function severityAccent(severity: NotificationItem["severity"]) {
  switch (severity) {
    case "error":
      return "rgba(255, 110, 110, 0.9)";
    case "warn":
      return "rgba(255, 184, 77, 0.9)";
    default:
      return "rgba(84, 209, 255, 0.92)";
  }
}

// Coerce any response (incl. an off-shape proxy error body) into a safe feed —
// used by every setter so feed.items is ALWAYS an array and render can't throw.
function normalizeFeed(data: unknown): NotificationsResponse {
  const d = data as Partial<NotificationsResponse> | null;
  return {
    items: Array.isArray(d?.items) ? d!.items : [],
    unreadCount: typeof d?.unreadCount === "number" ? d!.unreadCount : 0,
  };
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [feed, setFeed] = useState<NotificationsResponse>({ items: [], unreadCount: 0 });

  const unreadIds = useMemo(
    () => feed.items.filter((item) => !item.read).map((item) => item.id),
    [feed.items],
  );

  const loadFeed = useCallback(async () => {
    try {
      const data = await api<NotificationsResponse>("/api/notifications");
      // The bell lives in the header, outside the route ErrorBoundary, so an
      // off-shape response would throw in render and blank the app — normalize.
      setFeed(normalizeFeed(data));
    } catch {
      // A failed poll keeps the last-good feed; it must never crash the shell.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFeed();
    const timer = window.setInterval(() => {
      void loadFeed();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadFeed]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const markRead = useCallback(
    async (ids: string[]) => {
      if (!ids.length) {
        return;
      }
      setUpdating(true);
      try {
        const data = await api<NotificationsResponse>("/api/notifications/read", {
          method: "POST",
          body: JSON.stringify({ ids }),
        });
        setFeed(normalizeFeed(data));
      } catch {
        // A failed mark-read leaves items unread; it must never crash the shell
        // or abort the click-through navigation that awaits this.
      } finally {
        setUpdating(false);
      }
    },
    [],
  );

  async function handleNotificationClick(item: NotificationItem) {
    if (!item.read) {
      await markRead([item.id]);
    }
    setOpen(false);
    if (item.href) {
      navigate(item.href);
    }
  }

  return (
    <div ref={rootRef} style={styles.root}>
      <button
        type="button"
        style={styles.trigger}
        onClick={() => setOpen((current) => !current)}
        aria-label="Open notifications"
        aria-expanded={open}
      >
        <BellIcon />
        {feed.unreadCount > 0 && (
          <span style={styles.badge}>{feed.unreadCount > 9 ? "9+" : String(feed.unreadCount)}</span>
        )}
      </button>

      {open && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <div style={styles.panelEyebrow}>Alerts</div>
              <div style={styles.panelTitle}>Notifications</div>
            </div>
            <button
              type="button"
              style={styles.markAll}
              onClick={() => void markRead(unreadIds)}
              disabled={!unreadIds.length || updating}
            >
              Mark all read
            </button>
          </div>

          <div style={styles.list}>
            {loading ? (
              <div style={styles.emptyState}>Loading notifications...</div>
            ) : feed.items.length ? (
              feed.items.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  style={{
                    ...styles.item,
                    ...(item.read ? styles.itemRead : {}),
                    borderLeftColor: severityAccent(item.severity),
                  }}
                  onClick={() => void handleNotificationClick(item)}
                >
                  <div style={styles.itemHeader}>
                    <span style={styles.itemKind}>{item.kindLabel || item.kind.replace(/_/g, " ")}</span>
                    <span style={styles.itemTime}>{formatRelative(item.createdAt)}</span>
                  </div>
                  <div style={styles.itemTitle}>{item.title}</div>
                  <div style={styles.itemMessage}>{item.message}</div>
                  {!item.read && <span style={styles.unreadDot} />}
                </button>
              ))
            ) : (
              <div style={styles.emptyState}>No active alerts right now.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg width="18" height="18" style={{ width: "1.125rem", height: "1.125rem" }} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3.75a4.5 4.5 0 0 0-4.5 4.5v1.29c0 .85-.28 1.67-.8 2.34l-1.29 1.66a1.5 1.5 0 0 0 1.18 2.42h10.82a1.5 1.5 0 0 0 1.18-2.42l-1.29-1.66a3.75 3.75 0 0 1-.8-2.34V8.25a4.5 4.5 0 0 0-4.5-4.5Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.75 18.75a2.25 2.25 0 0 0 4.5 0"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    position: "relative",
  },
  trigger: {
    position: "relative",
    width: "2.7rem",
    height: "2.7rem",
    borderRadius: "var(--r-md)",
    border: "1px solid var(--rule)",
    background: "var(--ink-2)",
    color: "var(--text-default)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  badge: {
    position: "absolute",
    top: "-0.15rem",
    right: "-0.15rem",
    minWidth: "1.2rem",
    height: "1.2rem",
    padding: "0 0.3rem",
    borderRadius: "999px",
    background: "var(--flag-red)",
    color: "#fff",
    fontSize: "0.8125rem",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px solid rgba(9, 15, 26, 0.92)",
  },
  panel: {
    position: "absolute",
    top: "calc(100% + 0.75rem)",
    right: 0,
    width: "22rem",
    maxHeight: "28rem",
    overflow: "hidden",
    borderRadius: "var(--r-lg)",
    border: "1px solid rgba(148, 163, 184, 0.16)",
    background: "linear-gradient(180deg, rgba(11, 17, 28, 0.98), rgba(8, 13, 22, 0.97))",
    boxShadow: "0 24px 64px rgba(1, 7, 16, 0.35)",
    zIndex: 120,
  },
  panelHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "1rem",
    padding: "1rem 1rem 0.85rem",
    borderBottom: "1px solid rgba(148, 163, 184, 0.1)",
  },
  panelEyebrow: {
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "var(--text-secondary)",
  },
  panelTitle: {
    marginTop: "0.22rem",
    fontSize: "1rem",
    fontWeight: 800,
    color: "var(--text-strong)",
  },
  markAll: {
    border: "none",
    background: "none",
    color: "var(--live-strong)",
    fontSize: "0.8125rem",
    fontWeight: 700,
    cursor: "pointer",
    padding: 0,
  },
  list: {
    display: "grid",
    gap: "0.6rem",
    padding: "0.85rem",
    maxHeight: "22rem",
    overflowY: "auto",
  },
  item: {
    position: "relative",
    width: "100%",
    textAlign: "left",
    padding: "0.8rem 0.9rem 0.8rem 1rem",
    borderRadius: "16px",
    border: "1px solid rgba(148, 163, 184, 0.12)",
    borderLeftWidth: "3px",
    background: "rgba(255, 255, 255, 0.04)",
    color: "inherit",
    cursor: "pointer",
  },
  // Read items lose the tint and the dot; fading them would fail contrast (#986).
  itemRead: {
    background: "transparent",
  },
  itemHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.8rem",
  },
  itemKind: {
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "var(--text-secondary)",
  },
  itemTime: {
    fontSize: "0.8125rem",
    color: "var(--text-muted)",
    whiteSpace: "nowrap",
  },
  itemTitle: {
    marginTop: "0.42rem",
    fontSize: "0.92rem",
    fontWeight: 800,
    color: "var(--text-strong)",
  },
  itemMessage: {
    marginTop: "0.32rem",
    fontSize: "0.82rem",
    lineHeight: 1.45,
    color: "var(--text-secondary)",
    paddingRight: "1rem",
  },
  unreadDot: {
    position: "absolute",
    top: "0.95rem",
    right: "0.95rem",
    width: "0.45rem",
    height: "0.45rem",
    borderRadius: "999px",
    background: "var(--live-strong)",
  },
  emptyState: {
    padding: "1rem 0.2rem",
    fontSize: "0.88rem",
    lineHeight: 1.5,
    color: "var(--text-secondary)",
  },
};
